import React, { memo, useCallback, useEffect, useState } from 'react';
import cn from 'classnames';

import { useMutateSetCmDefaultVendor } from '@hooks/cmModule/useMutateSetCmDefaultVendor';
import { useQueryCmModuleVendors } from '@hooks/cmModule/useQueryCmModuleVendors';

import { configurationManagement } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import { setSelectedVendor } from '@slices/configurationManagement';

import toSnakeCase from '@utils/toSnakeCase';

import { TSelectOption } from '@interfaces/components/componentTypes';
import { IMenuItem } from '@components/common/SideBar/interfaces';
import { TSelectOptionType } from 'components/common/Select/Select.types';

import { TVendorsValues } from '@enums/common';

import Select from 'components/common/Select/Select';

import styles from '@components/common/SideBar/SideBar.module.scss';

interface ISideBarItemsProps {
	groupItems: IMenuItem[] | undefined;
	handleRouteNavigation: (route: string, isActive: boolean) => void;
	isVendorSpecific?: boolean;
}

const SideBarItems = ({
	groupItems = [],
	handleRouteNavigation,
	isVendorSpecific = false,
}: ISideBarItemsProps) => {
	const dispatch = useAppDispatch();
	const { selectedVendor } = useAppSelector(configurationManagement);

	const { cmModuleVendorOptions, isLoadingCmModuleVendors } = useQueryCmModuleVendors();
	const { setDefaultVendor, isUpdatingDefaultVendor } = useMutateSetCmDefaultVendor();

	const [isVendorSelectorDisabled, setIsVendorSelectorDisabled] = useState(false);

	const onChangeSelectedVendor = useCallback((selectedVendor: TSelectOption<TVendorsValues>) => {
		dispatch(setSelectedVendor(selectedVendor));
	}, [dispatch]);

	useEffect(() => {
		if (!selectedVendor && cmModuleVendorOptions.length) {
			const defaultVendor = cmModuleVendorOptions.find(o => o.isDefault) || cmModuleVendorOptions[0];

			setIsVendorSelectorDisabled(cmModuleVendorOptions.length === 1);
			dispatch(setSelectedVendor(defaultVendor));
		}
	}, [dispatch, selectedVendor, cmModuleVendorOptions]);

	const setDefaultCMVendor = (option: TSelectOptionType) => {
		setDefaultVendor(option);
	};

	return (
		<div className={styles.menuSubList}>
			{isVendorSpecific && (
				<Select
					className={styles.vendorSelector}
					favoriteTooltip='Set vendor as default'
					handleChange={onChangeSelectedVendor}
					isFavorite
					isLoading={isLoadingCmModuleVendors || isUpdatingDefaultVendor}
					isReadOnly={isVendorSelectorDisabled}
					isWithoutUseForms
					menuPortalTarget={document.body}
					minHeight='unset'
					name='vendor'
					options={cmModuleVendorOptions}
					placeholder='Select vendor'
					setFavorite={setDefaultCMVendor}
					value={selectedVendor}
				/>
			)}
			{groupItems.map((groupItem, index) => {
				const isActive = location.pathname.includes(groupItem.route);

				const itemClasses = cn(styles.menuItem, {
					[styles.active]: isActive,
				});

				const itemDataTestId = toSnakeCase(groupItem.title);

				const onGroupItemClick = () => {
					if (!groupItem.url) {
						handleRouteNavigation(groupItem.route, isActive);
					} else {
						const newWindow = window.open(groupItem.url, '_blank');

						if (newWindow) {
							newWindow.focus();
						}
					}
				};

				return (
					<div
						key={`${groupItem.title}-${index}`}
						className={itemClasses}
						data-testid={`group_item_${itemDataTestId}`}
						onClick={onGroupItemClick}
					>
						<span className={styles.title}>
							{groupItem.title}
							{groupItem.titleIcon && groupItem.titleIcon()}
						</span>
					</div>
				);
			})}
		</div>
	);
};

export default memo(SideBarItems);
